import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { MapPickerDialog } from "@/components/MapPickerDialog";
import { FeedPathTag } from "@/components/FeedPathTag";
import { VersionTag } from "@/components/VersionTag";
import { SyncFieldBadge } from "@/components/SyncFieldBadge";
import { Pencil, Save, MapPin, ExternalLink } from "lucide-react";

function Field({ label, feedPath, syncPath, editing, ddmMigrated, syncedFields, className = "", children }) {
  return (
    <div className={className}>
      <Label className="data-label mb-1 flex items-center gap-1">
        {label}
        <FeedPathTag path={feedPath} />
        <SyncFieldBadge path={syncPath} editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields} />
      </Label>
      {children}
    </div>
  );
}

const localizedValue = (obj) =>
  (obj && (obj["de-DE"] || Object.values(obj)[0])) || "";

/** Branch info card + coordinates edit dialog. */
export function BranchInfoCard({ dealer, branch, canManage = false, canEditCoords, onBranchSaved, syncedFields = [] }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const ddmMigrated = dealer.management_migrated_to_ddm === true;

  const streetVal = localizedValue(branch.street);
  const cityVal = localizedValue(branch.city);

  const start = () => {
    setDraft({
      street: streetVal,
      zip_code: branch.zip_code || "",
      city: cityVal,
      is_sales: !!branch.is_sales,
      is_service: !!branch.is_service,
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/branches/${branch.id}`, {
        zip_code: draft.zip_code,
        street: { "de-DE": draft.street },
        city: { "de-DE": draft.city },
        is_sales: draft.is_sales,
        is_service: draft.is_service,
      });
      onBranchSaved(data);
      setEditing(false);
      toast.success(t("dealership.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const saveCoordinates = async ({ latitude, longitude }) => {
    const { data } = await client.patch(`/branches/${branch.id}/coordinates`, { latitude, longitude });
    onBranchSaved(data);
    toast.success(t("dealership.saved"));
  };

  const gmapsLink = branch.latitude && branch.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${branch.latitude},${branch.longitude}`
    : null;

  return (
    <Card className="mb-6 p-6" data-testid="branch-info-card">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="flex items-center gap-1.5 text-lg font-semibold">{t("dealership.branchInfo")} <VersionTag version={dealer.card_versions?.opening_hours} testId="version-tag-opening_hours" /></h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("dealership.branchInfoSub")}
          </p>
        </div>
        {canManage && (
          !editing ? (
            <Button data-testid="edit-branch-btn" variant="ghost" size="sm" className="gap-1.5" onClick={start}>
              <Pencil className="h-3.5 w-3.5" />
              {t("common.edit")}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                {t("common.cancel")}
              </Button>
              <Button data-testid="save-branch-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
                <Save className="h-3.5 w-3.5" />
                {t("common.save")}
              </Button>
            </div>
          )
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Field label={t("dealership.street")} feedPath="Dealer.Street" syncPath="branch.street" className="md:col-span-2"
          editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields}>
          <Input data-testid="branch-street" disabled={!editing || saving}
            value={editing ? draft.street : streetVal}
            onChange={(e) => setDraft({ ...draft, street: e.target.value })} />
        </Field>
        <Field label={t("dealership.zip")} feedPath="Dealer.ZipCode" syncPath="branch.zip_code"
          editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields}>
          <Input data-testid="branch-zip" disabled={!editing || saving} maxLength={5} pattern="\d{5}"
            value={editing ? draft.zip_code : branch.zip_code || ""}
            onChange={(e) => setDraft({ ...draft, zip_code: e.target.value })} />
        </Field>
        <Field label={t("dealership.city")} feedPath="Dealer.City" syncPath="branch.city"
          editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields}>
          <Input data-testid="branch-city" disabled={!editing || saving}
            value={editing ? draft.city : cityVal}
            onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
        </Field>
      </div>

      <div className="mt-6 border-t border-border pt-6">
        <p className="data-label mb-3">{t("dealership.feedData")}</p>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm" data-testid="branch-is-sales-label">
            <Checkbox data-testid="branch-is-sales" checked={editing ? draft.is_sales : !!branch.is_sales}
              disabled={!editing || saving}
              onCheckedChange={(v) => setDraft({ ...draft, is_sales: !!v })} />
            {t("dealership.salesCapability")}
            <FeedPathTag path="Dealer.IsSales" />
            <SyncFieldBadge path="branch.is_sales" editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields} />
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="branch-is-service-label">
            <Checkbox data-testid="branch-is-service" checked={editing ? draft.is_service : !!branch.is_service}
              disabled={!editing || saving}
              onCheckedChange={(v) => setDraft({ ...draft, is_service: !!v })} />
            {t("dealership.serviceCapability")}
            <FeedPathTag path="Dealer.IsService" />
            <SyncFieldBadge path="branch.is_service" editing={editing} ddmMigrated={ddmMigrated} syncedFields={syncedFields} />
          </label>
        </div>
      </div>

      <div className="mt-6 rounded-md border border-border bg-muted/30 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="data-label mb-1 flex items-center gap-1">
              {t("dealership.coordinates")}
              <FeedPathTag path="Dealer.Latitude / Dealer.Longitude" />
            </p>
            <p className="font-mono text-sm tabular-nums" data-testid="branch-coords">
              {branch.latitude != null && branch.longitude != null
                ? `${branch.latitude.toFixed(6)}, ${branch.longitude.toFixed(6)}`
                : "—"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {gmapsLink && (
              <a data-testid="coords-gmaps-link" href={gmapsLink} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline">
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
                {t("dealership.viewOnMaps")}
              </a>
            )}
            {canEditCoords && (
              <Button data-testid="edit-coords-btn" size="sm" variant="outline" className="gap-1.5" onClick={() => setMapOpen(true)}>
                <MapPin className="h-3.5 w-3.5" />
                {t("dealership.coordinatesEdit")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {mapOpen && (
        <MapPickerDialog open={mapOpen} onOpenChange={setMapOpen} branch={branch} dealer={dealer} onSave={saveCoordinates} />
      )}
    </Card>
  );
}
